export interface InstallerInfo {
  platform: 'windows' | 'macos' | 'linux';
  filename: string;
  url: string;
  size?: number;
}

export class InstallerService {
  private baseUrl = '/installers'; // From public folder

  detectPlatform(): 'windows' | 'macos' | 'linux' {
    const platform = navigator.platform.toLowerCase();
    const userAgent = navigator.userAgent.toLowerCase();

    if (platform.includes('win') || userAgent.includes('windows')) {
      return 'windows';
    }
    if (platform.includes('mac') || userAgent.includes('macintosh')) {
      return 'macos';
    }
    return 'linux';
  }

  getInstallerInfo(): InstallerInfo {
    const platform = this.detectPlatform();
    const extensions = {
      windows: 'exe',
      macos: 'pkg',
      linux: 'deb' // or 'rpm'
    };

    const filename = `device-bridge-setup.${extensions[platform]}`;
    const url = `${this.baseUrl}/${platform}/${filename}`;

    return {
      platform,
      filename,
      url
    };
  }

  async downloadInstaller(): Promise<Blob> {
    const info = this.getInstallerInfo();

    try {
      const response = await fetch(info.url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Установщик для ${this.getPlatformName(info.platform)} не найден. Обратитесь к администратору.`);
        }
        throw new Error(`Не удалось загрузить установщик: ${response.statusText}`);
      }
      return await response.blob();
    } catch (error: any) {
      if (error.message && (error.message.includes('не найден') || error.message.includes('Не удалось'))) {
        throw error;
      }
      throw new Error(`Установщик недоступен. ${error.message || 'Проверьте подключение к интернету или обратитесь к администратору.'}`);
    }
  }

  async triggerDownload(): Promise<void> {
    const info = this.getInstallerInfo();
    const blob = await this.downloadInstaller();

    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = info.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  getInstallInstructions(platform: 'windows' | 'macos' | 'linux'): string {
    const instructions = {
      windows: `1. Установщик загружен в папку "Загрузки"
2. Найдите и дважды щелкните "device-bridge-setup.exe"
3. Следуйте инструкциям мастера установки
4. Вам может быть предложено предоставить права администратора
5. Сервис запустится автоматически после установки
6. Вернитесь на эту страницу и нажмите "Проверить установку"`,
      macos: `1. Установщик загружен в папку "Загрузки"
2. Найдите и дважды щелкните "device-bridge-installer.pkg"
3. Следуйте инструкциям мастера установки
4. Вам может быть предложено ввести пароль администратора
5. Сервис запустится автоматически после установки
6. Вернитесь на эту страницу и нажмите "Проверить установку"`,
      linux: `1. Установщик загружен в папку "Загрузки"
2. Откройте терминал и перейдите в папку Загрузки:
   cd ~/Downloads
3. Установите пакет:
   sudo dpkg -i device-bridge-setup.deb
   # Или для RPM:
   # sudo rpm -i device-bridge-setup.rpm
4. Сервис запустится автоматически
5. Вернитесь на эту страницу и нажмите "Проверить установку"`
    };

    return instructions[platform].trim();
  }

  getPlatformName(platform: 'windows' | 'macos' | 'linux'): string {
    const names = {
      windows: 'Windows',
      macos: 'macOS',
      linux: 'Linux'
    };
    return names[platform];
  }
}

export const installerService = new InstallerService();

